import Phaser from 'phaser';

const PATH_WIDTH = 20;
const PATH_SAMPLES = 50;

export class PathHitArea {
    private pathZone?: Phaser.GameObjects.Zone;
    private debugGraphics?: Phaser.GameObjects.Graphics;

    constructor(
        private scene: Phaser.Scene,
        private debugMode: boolean
    ) { }

    public create(trace: Phaser.Curves.Path): Phaser.GameObjects.Zone {
        const points = this.samplePath(trace, PATH_SAMPLES);
        const expandedPoints = this.expandPathPoints(points, PATH_WIDTH);
        const polygonPoints: Phaser.Types.Math.Vector2Like[] = expandedPoints.map(p => ({ x: p.x, y: p.y }));
        const hitArea = new Phaser.Geom.Polygon(polygonPoints as any);

        this.pathZone = this.scene.add.zone(0, 0, 1, 1);
        this.scene.children.sendToBack(this.pathZone);

        this.pathZone.setOrigin(0, 0);
        this.pathZone.setInteractive(hitArea, Phaser.Geom.Polygon.Contains, true);

        if (this.debugMode) {
            this.renderDebug(polygonPoints);
        }

        this.pathZone.setDepth(1);
        if (this.debugGraphics) {
            this.debugGraphics.setDepth(1);
        }

        return this.pathZone;
    }

    private samplePath(trace: Phaser.Curves.Path, segments: number): Phaser.Math.Vector2[] {
        const points: Phaser.Math.Vector2[] = [];
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            points.push(trace.getPoint(t));
        }
        return points;
    }

    private expandPathPoints(points: Phaser.Math.Vector2[], width: number): Phaser.Math.Vector2[] {
        const expanded: Phaser.Math.Vector2[] = [];

        // Outer edge
        expanded.push(...this.offsetPoints(points, width, 1));

        // Inner edge (reversed)
        expanded.push(...this.offsetPoints(points, width, -1).reverse());

        return expanded;
    }

    private offsetPoints(
        points: Phaser.Math.Vector2[],
        width: number,
        direction: number
    ): Phaser.Math.Vector2[] {
        const offseted: Phaser.Math.Vector2[] = [];

        for (let i = 0; i < points.length; i++) {
            const prev = points[i - 1] || points[i];
            const curr = points[i];
            const next = points[i + 1] || points[i];

            const perp = this.calculatePerpendicular(prev, curr, next);
            const scaled = perp.scale(width * direction);

            offseted.push(new Phaser.Math.Vector2(curr.x + scaled.x, curr.y + scaled.y));
        }

        return offseted;
    }

    private calculatePerpendicular(
        prev: Phaser.Math.Vector2,
        curr: Phaser.Math.Vector2,
        next: Phaser.Math.Vector2
    ): Phaser.Math.Vector2 {
        const dir1 = new Phaser.Math.Vector2(curr.x - prev.x, curr.y - prev.y).normalize();
        const perp1 = new Phaser.Math.Vector2(-dir1.y, dir1.x);

        const dir2 = new Phaser.Math.Vector2(next.x - curr.x, next.y - curr.y).normalize();
        const perp2 = new Phaser.Math.Vector2(-dir2.y, dir2.x);

        return new Phaser.Math.Vector2(perp1.x + perp2.x, perp1.y + perp2.y).normalize();
    }

    private renderDebug(points: Phaser.Types.Math.Vector2Like[]): void {
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.lineStyle(2, 0x00ff00, 0.7);
        this.debugGraphics.fillStyle(0x00ff00, 0.2);
        this.debugGraphics.fillPoints(points, true);
        this.debugGraphics.strokePoints(points, true);
        this.debugGraphics.setDepth(1);
    }

    public destroy(): void {
        this.pathZone?.destroy();
        this.debugGraphics?.destroy();
    }

    public bringToFront(): void {
        if (this.pathZone) {
            this.pathZone.setDepth(10);
        }
        if (this.debugGraphics) {
            this.debugGraphics.setDepth(10);
        }
    }

    public sendToBack(): void {
        if (this.pathZone) {
            this.pathZone.setDepth(1);
        }
        if (this.debugGraphics) {
            this.debugGraphics.setDepth(1);
        }
    }
}